import { TCreateLearnerPlanReqDto, TCreateLearnerReqDto, TUpdateLearnerReqDto } from "@/api/services/main/learner/dtos/create-learner-req.dto";
import { TGetLearnerDto } from "@/api/services/main/learner/dtos/get-learner.dto";
import { TLearnerForm, TLearnerPlan } from "@/features/learner/types/learnerForm";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT_FOR_API, YEAR_MONTH_FORMAT } from "@/constants/datetime";

/**
 * Convert form data to API request DTO
 */
export const convertLearnerFormDataReq = (data: TLearnerForm): TCreateLearnerReqDto => {
  return {
    nationalityId: Number(data.nationalityId),
    industryId: Number(data.industryId),
    rsoCompanyId: data.rsoCompanyId ? Number(data.rsoCompanyId) : null,
    fullName: data.fullName,
    nickName: data.nickName ?? null,
    gender: data.gender,
    prefecture: data.prefecture ?? null,
    city: data.city ?? null,
    clause1StartDate: data.clause1StartDate ? dayjs(data.clause1StartDate).format(DATE_FORMAT_FOR_API) : null,
    blankPeriodMonths: data.blankPeriodMonths,
    japaneseLanguageLevel: data.japaneseLanguageLevel ?? null,
    lastExamDate: data.lastExamDate ? dayjs(data.lastExamDate).format(DATE_FORMAT_FOR_API) : null,
    lastExamScore: data.lastExamScore ?? null,
    memo: data.memo ?? null,
    isUsingLearningApp: data.isUsingLearningApp ?? null,
    isProcedureManaged: data.isProcedureManaged ?? null,
    learnerPlan: {
      planStatus: data.learnerPlan?.planStatus ?? null,
      curriculumProgressStatus: data.learnerPlan?.curriculumProgressStatus ?? null,
      learningStartDate: data.learnerPlan?.learningStartDate ? dayjs(data.learnerPlan.learningStartDate).format(DATE_FORMAT_FOR_API) : null,
      examScheduledYearMonth: dayjs(data.learnerPlan.examScheduledYearMonth).format(YEAR_MONTH_FORMAT),
      isSelfStudyMode: data.learnerPlan?.isSelfStudyMode ?? null,
      partTypes: data.learnerPlan?.partTypes ?? null,
      examId: data.learnerPlan?.examId ? Number(data.learnerPlan.examId) : null,
    },
  };
};

/**
 * Convert form data to API request DTO for update (excludes industryId).
 * learningStartDate is included so it can be edited or cleared (null) when the learning app is turned off.
 */
export const convertLearnerFormDataUpdateReq = (data: TLearnerForm): TUpdateLearnerReqDto => {
  const { industryId: _industryId, ...rest } = convertLearnerFormDataReq(data);
  return rest;
};

/**
 * Convert API response to form data for edit page
 */
export const convertResToFormData = (data: TGetLearnerDto): Partial<TLearnerForm> | undefined => {
  if (!data) return undefined;

  return {
    fullName: data.fullName,
    nickName: data.nickName ?? null,
    nationalityId: data.nationalityId?.toString(),
    gender: data.gender?.toString(),
    prefecture: data.prefecture ?? null,
    city: data.city ?? null,
    clause1StartDate: data.clause1StartDate ? dayjs(data.clause1StartDate) : null,
    blankPeriodMonths: data.blankPeriodMonths,
    industryId: data.industryId?.toString(),
    japaneseLanguageLevel: data.japaneseLanguageLevel ?? null,
    lastExamDate: data.lastExamDate ? dayjs(data.lastExamDate) : null,
    lastExamScore: data.lastExamScore ?? null,
    memo: data.memo ?? null,
    userId: data?.userId ?? null,
    rsoCompanyId: data?.rsoCompanyId?.toString() ?? null,
    learnerPlan: {
      planStatus: data.learnerPlans[0]?.planStatus ?? null,
      curriculumProgressStatus: data.learnerPlans[0]?.curriculumProgressStatus ?? null,
      learningStartDate: data.learnerPlans[0]?.learningStartDate ? dayjs(data.learnerPlans[0].learningStartDate) : null,
      examScheduledYearMonth: dayjs(data.learnerPlans[0].examScheduledYearMonth),
      isSelfStudyMode: data.learnerPlans[0]?.isSelfStudyMode ?? null,
      partTypes: data.learnerPlans[0]?.partTypes ?? null,
      examId: data.learnerPlans[0]?.examId?.toString(),
      exam: data.learnerPlans[0]?.exam ?? null,
    },
    learnerPlansCount: data.learnerPlansCount ?? 0,
    nationality: data.nationality,
    industry: data.industry,
    user: data.user,
    rsoCompany: data.rsoCompany,
    redirectUrl: data.redirectUrl ?? null,
    lastLoginTime: data.lastLoginTime ?? null,
    isArchive: data.isArchive ?? false,
    isUsingLearningApp: data.isUsingLearningApp ?? null,
    isProcedureManaged: data.isProcedureManaged ?? null,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

/**
 * Convert learner plan form data to API request DTO
 */
export const convertLearnerPlanFormDataReq = (data: TLearnerPlan): TCreateLearnerPlanReqDto => {
  return {
    planStatus: data.planStatus ?? null,
    curriculumProgressStatus: data.curriculumProgressStatus ?? null,
    learningStartDate: dayjs(data.learningStartDate).format(DATE_FORMAT_FOR_API),
    examScheduledYearMonth: dayjs(data.examScheduledYearMonth).format(YEAR_MONTH_FORMAT),
    isSelfStudyMode: data.isSelfStudyMode ?? null,
    partTypes: data.partTypes ?? null,
    examId: data.examId ? Number(data.examId) : null,
  };
};
